import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	CircularProgress,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RuleIcon from '@mui/icons-material/Rule';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DataGrid, esES } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getProviders } from '../../actions/provider';
import { formatDateTimeMX } from '../../helpers/hours';
import {
	columnStyleCell,
	getFullName,
	handleConfirmModal,
	handleError,
	handleSuccess,
	money,
	rowStyle,
} from '../../helpers/utils';
import {
	deleteExternalTollingRequest,
	handleGeneratEtrPDF,
	updateExternalTollingRequest,
} from '../../actions/externalTollingRequest';
import { SearcherETR } from './SearcherETR';
import Label from '../utils/Label';

const defaultEndpoint =
	'/api/order/external-tolling-request/?ordering=-created_at&page_size=5';
const searchIconStyle = { color: '#143f6c', cursor: 'pointer' };

// External tolling table
export const ExternalTollingTable = () => {
	// Hook for navigation
	const navigate = useNavigate();

	const { user_type } = useSelector((state) => state.user);

	// Show searcher or not
	const [showSearcher, setShowSearcher] = useState(false);

	// Loading state
	const [isLoading, setLoading] = useState(false);

	// Rows
	const [rowsState, setRowsState] = useState({
		isLoading: false,
		data: [],
		total: 0,
		page: 1,
		lastPage: 1,
		pageSize: 5,
		next: defaultEndpoint,
		previous: null,
	});

	const generateRows = async (newData = false) => {
		let newProviders = [];
		let total = 0;
		let data = null;

		const moveForward = rowsState.page >= rowsState.lastPage;

		setLoading(true);

		if (newData) {
			newProviders = newData.results;
			total = newData.count;
		} else {
			data = (
				await getProviders(
					newData === null
						? defaultEndpoint
						: moveForward
						? rowsState.next
						: rowsState.previous
				)
			).data;
			newProviders = data.results;
			total = data.count;
		}

		if (newProviders?.length === 0) {
			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: [],
				total: 0,
				next: data?.next,
				previous: data?.previous,
			}));
		} else {
			const res =
				newProviders?.map((item) => ({
					...item,
					created_at: formatDateTimeMX(item.created_at),
				})) || [];

			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: res,
				total: total,
				pageSize: newData === null ? 5 : old.pageSize,
				next: data?.next,
				previous: data?.previous,
			}));
		}

		setLoading(false);
	};

	// Handle delete user with modal confirmation
	const handleDeleteETR = (id) => {
		handleConfirmModal(
			'¡Precaución!',
			'¿Estás seguro de eliminar permanentemente la solicitud?',
			'warning',
			'Sí, eliminar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					deleteExternalTollingRequest(id).then(() => {
						// Feedback
						handleSuccess('Solicitud eliminada');
						// Remove from UI
						const newRows = rowsState.data.filter(
							(item) => item.id !== id
						);
						setRowsState({
							...rowsState,
							data: newRows,
						});
					});
				}
			})
			.catch((error) => handleError(error));
	};

	const handleCancelETR = (etr) => {
		handleConfirmModal(
			'¡Precaución!',
			'¿Estás seguro de cancelar la solicitud?',
			'warning',
			'Sí, cancelar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					const newValues = {
						cancelled: true,
						approved: false,
					};
					updateExternalTollingRequest(etr.id, {
						...etr,
						provider: etr.provider.id,
						created_by: etr.created_by.id,
						...newValues,
					}).then(() => {
						// Feedback
						handleSuccess('Solicitud cancelada');
						// Remove from UI
						const newRows = rowsState.data.map((item) => {
							if (item.key === etr.key) {
								return {
									...item,
									...newValues,
								};
							}
							return item;
						});
						setRowsState({
							...rowsState,
							data: newRows,
						});
					});
				}
			})
			.catch((error) => handleError(error));
	};

	useEffect(() => {
		generateRows();
	}, [rowsState.page, rowsState.pageSize]);

	const whichStatus = (item) => {
		if (item.cancelled) return 'Cancelado';
		if (item.is_done) return 'Completado';
		if (item.approved) return 'Por recibir';
		return 'Por aprobar';
	};

	const whichColor = (item) => {
		if (item.cancelled) return 'error';
		if (item.is_done) return 'success';
		if (item.approved) return 'primary';
		return 'warning';
	};

	// Define columns
	const columns = [
		{
			field: 'col8',
			headerName: 'Acciones',
			width: 220,
			...columnStyleCell,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					{!row?.cancelled && (
						<Tooltip title='Ver solicitud'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									navigate(
										`/dashboard/maquila-externa/${row.id}`
									);
								}}
							>
								<InfoIcon />
							</IconButton>
						</Tooltip>
					)}
					{/* {[1, 2, 5].includes(user_type.id) && ( */}
					{!row?.cancelled && !row?.is_done && (
						<Tooltip title='Editar solicitud'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									navigate(
										`/dashboard/editar/maquila-externa/${row.id}`
									);
								}}
							>
								<EditIcon />
							</IconButton>
						</Tooltip>
					)}
					{row?.approved && !row?.is_done && (
						<Tooltip title='Recibir solicitud'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									navigate(
										`/dashboard/maquila-externa-recepcion/${row.id}`
									);
								}}
							>
								<RuleIcon />
							</IconButton>
						</Tooltip>
					)}
					{!row?.cancelled && (
						<Tooltip title='Descargar PDF'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									handleGeneratEtrPDF(row.pdf_payload);
								}}
							>
								<PictureAsPdfIcon />
							</IconButton>
						</Tooltip>
					)}
					{!row?.cancelled && !row?.is_done && (
						<Tooltip title='Cancelar solicitud'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => handleCancelETR(row)}
							>
								<DoDisturbAltIcon />
							</IconButton>
						</Tooltip>
					)}
					{/* )} */}
					{user_type.id === 2 && !row.cancelled && !row?.is_done && (
						<Tooltip title='Eliminar solicitud'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='delete'
								color='primary'
								onClick={() => handleDeleteETR(row.id)}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</>
			),
		},
		{
			field: 'key',
			headerName: 'Clave',
			width: 150,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.key}>
					<span className='table-cell-trucate'>{row.key}</span>
				</Tooltip>
			),
		},
		{
			field: 'approved',
			headerName: 'Estatus',
			width: 140,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Label color={whichColor(row)}>{whichStatus(row)}</Label>
				</span>
			),
		},
		{
			field: 'tolling_type',
			headerName: 'Tipo de maquila',
			width: 150,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.tolling_type}>
					<span className='table-cell-trucate'>
						{row.tolling_type}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'total_pieces',
			headerName: 'No. total de unidades',
			width: 150,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>{row.total_pieces}</span>
			),
		},
		{
			field: 'total_price',
			headerName: 'Precio total',
			width: 150,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{money(row.total_price)}
				</span>
			),
		},
		{
			field: 'provider',
			headerName: 'Proveedor',
			width: 250,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.provider.name}>
					<span className='table-cell-trucate'>
						{row.provider.name}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'created_by',
			headerName: 'Creado por',
			width: 200,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={getFullName(row.created_by)}>
					<span className='table-cell-trucate'>
						{getFullName(row.created_by)}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'created_at',
			headerName: 'Creado',
			width: 160,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.created_at}>
					<span className='table-cell-trucate'>{row.created_at}</span>
				</Tooltip>
			),
		},
	];

	return (
		<div
			style={{
				padding: '20px',
			}}
		>
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 40px 0',
					width: '100%',
					...columnStyleCell,
				}}
			>
				<Typography variant='h4' gutterBottom>
					Solicitudes de maquila externa
				</Typography>
				<Button
					variant='contained'
					style={{
						backgroundColor: '#143f6c',
					}}
					onClick={() => {
						navigate('/dashboard/maquila-externa');
					}}
				>
					Nueva solicitud
				</Button>
			</Stack>

			<hr />

			<Typography
				variant='h6'
				gutterBottom
				style={{ ...rowStyle, margin: '20px 0 10px 0' }}
			>
				Búsqueda global{' '}
				<div
					onClick={() => setShowSearcher(!showSearcher)}
					style={{ margin: '0 10px' }}
				>
					{showSearcher ? (
						<SearchOffIcon style={searchIconStyle} />
					) : (
						<SearchIcon style={searchIconStyle} />
					)}
				</div>
			</Typography>

			{/* Order searcher */}
			{showSearcher && (
				<SearcherETR
					generateRows={generateRows}
					handleClose={() => setShowSearcher(false)}
					rowsState={rowsState}
					setRowsState={setRowsState}
				/>
			)}

			{isLoading ? (
				<center style={{ padding: '20%' }}>
					<CircularProgress />
				</center>
			) : (
				<>
					{/* Datagrid table */}
					{rowsState.data.length > 0 ? (
						<DataGrid
							rows={rowsState.data}
							rowCount={rowsState.total}
							loading={rowsState.isLoading}
							rowsPerPageOptions={[5, 10, 20]}
							pagination
							autoHeight
							page={rowsState.page - 1}
							pageSize={rowsState.pageSize}
							paginationMode='server'
							onPageChange={(newPage) => {
								setRowsState((old) => ({
									...old,
									page: newPage + 1,
									lastPage: old.page,
								}));
							}}
							onPageSizeChange={(newPage) => {
								if (showSearcher) {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `${old.current}&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								} else {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `/api/order/external-tolling-request/?ordering=-created_at&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								}
							}}
							columns={columns}
							localeText={
								esES.components.MuiDataGrid.defaultProps
									.localeText
							}
						/>
					) : (
						<Alert severity='info'>
							No hay ninguna solicitud de maquila externa
						</Alert>
					)}
				</>
			)}
		</div>
	);
};
